<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Header, CustomButton, Table, TableIconText, ButtonType, FormDeleteAlertDialog, FormOrderDialog } from '@/components';
import { useFormStore } from '@/stores/forms';
import { formattedDate, DATE_FORMAT_2, formatTimeSpent } from '@/utils';
import { TableColumnType } from '../common/table/interfaces/TableColumnModel';
import { FormModel, type TimeSpentOnForm } from '@/models';

const formStore = useFormStore();

const openLinkIcon = () => ({
  icon: 'arrow-up-right-from-square',
  style: 'fas'
});

const cloneIcon = () => ({
  icon: 'copy',
  style: 'far'
});

const printIcon = () => ({
  icon: 'print',
  style: 'fas'
});

const showFormDeleteAlert = ref<boolean>(false);
const openCloseDeleteAlert = () => {
  showFormDeleteAlert.value = !showFormDeleteAlert.value;
};

const selectedRows = ref<FormModel[]>([]);

const deleteForms = async () => {
  await formStore.deleteForms(selectedRows.value);
  selectedRows.value = [];
  openCloseDeleteAlert();
  formStore.getStudyForms();
};

const openPublicForm = (identifier: string) => {
  window.open(`${import.meta.env.VITE_APP_FRONTEND_BASE_URL}/form/${identifier}`, '_blank');
};

const cloneForm = (form: FormModel) => {
  formStore.cloneForms(form);
};

const showFormOrder = ref<boolean>(false);
const openFormOrder = (reloadForms: boolean) => {
  showFormOrder.value = !showFormOrder.value;
  if (reloadForms === true) {
    formStore.getStudyForms();
  }
};

const router = useRouter();
const onFormClick = (form: FormModel) => {
  router.push(`form/${form.identifier}`);
};

const openPrintForm = (form: FormModel) => {
  window.open(`/form/${form.identifier}/print`, '_blank');
};

const showMinMaxTimeSpent = (timeSpentData?: TimeSpentOnForm) => {
  if (!timeSpentData || timeSpentData.minEntryTime === undefined || timeSpentData.maxEntryTime === undefined) {
    return '';
  }
  return `Minimum time spent: ${formatTimeSpent(timeSpentData.minEntryTime)},\nMaximum time spent: ${formatTimeSpent(timeSpentData.maxEntryTime)}`;
};
</script>

<template>
  <div class="forms-list">
    <div class="forms-wrapper">
      <div class="header">
        <Header title="Forms">
          <template #headerButtons>
            <div class="header-buttons">
              <CustomButton text="Form order"
                :icon="{style: 'fal', icon: 'arrow-down-arrow-up'}"
                v-if="selectedRows.length == 0 && formStore?.formList.length > 0"
                :type="ButtonType.OUTLINED"
                @onClick="openFormOrder"/>
              <CustomButton text="New form"
                :type="ButtonType.POSITIVE"
                :icon="{style: 'fas', icon: 'file'}"
                v-if="selectedRows.length == 0"
                :marginLeft="16"
                :loading="formStore.createFormLoader.isLoading"
                @onClick="formStore.postForm" />
              <CustomButton text="Delete"
                :icon="{style: 'fas', icon: 'trash'}"
                v-if="selectedRows.length > 0"
                :type="ButtonType.NEGATIVE"
                @onClick="openCloseDeleteAlert"/>
            </div>
          </template>
        </Header>
      </div>
      <div class="forms-table">
        <Table :columns="formStore.tableColumns"
          :data-list="formStore.formList.filter(form => !form.sharedForm)"
          :loading-state="formStore.isGettingForms.isLoading || formStore.cloneFormLoader.isLoading"
          :presortOrder="1"
          :selection="true"
          v-model:selectedRows="selectedRows"
          @onRowClick="onFormClick"
          presort="order">
          <template #bodyContent="{ data, col }">
            <TableIconText :text="data[col.key]"
              :icon="col.icon"
              v-if="col.type === TableColumnType.TEXT_WTIH_ICON"/>
            <div v-else-if="col.type === TableColumnType.SQAURE_BUTTON_WITH_ICON" class="hover-buttons">
              <CustomButton :text="col.key === 'open' ? 'New entry' : col.key === 'clone' ? 'Clone' : 'Print'"
                :icon="col.key === 'open' ? openLinkIcon() : col.key === 'clone' ? cloneIcon() : printIcon()"
                :type="ButtonType.VERTICAL_OUTLINED"
                @onClick="() => col.key === 'open' ? openPublicForm(data.identifier) : col.key === 'clone' ? cloneForm(data) : openPrintForm(data)"/>
            </div>
            <div v-else v-tooltip="col.key === 'timeSpentData' ? showMinMaxTimeSpent(data[col.key]) : ''">
                {{
                col.key === "createdAt" || col.key === "updatedAt"
                    ? formattedDate(data[col.key], DATE_FORMAT_2)
                    : col.key === "timeSpentData"
                    ? (formatTimeSpent(data[col.key].averageEntryTime) || 'No data')
                    : data[col.key]
                }}
            </div>
          </template>
          <template #emptyContent>
            <div class="empty-content-container">
              <div class="empty-content">
                <FontAwesomeIcon :icon="[`fal`, 'file']" class="file-icon" />
                <h3 class="no-entries-label">No forms created yet</h3>
                <div class="create-context-wrapper">
                  <div class="new-entry-text">To create a form click on</div>
                  <CustomButton text="New form"
                    :type="ButtonType.POSITIVE"
                    :icon="{style: 'fas', icon: 'file'}"
                    v-if="selectedRows.length == 0"
                    :marginLeft="16"
                    :loading="formStore.createFormLoader.isLoading"
                    @onClick="formStore.postForm" />
                </div>
              </div>
            </div>
          </template>
        </Table>
      </div>
    </div>
  </div>
  <FormDeleteAlertDialog v-if="showFormDeleteAlert"
    :visible="showFormDeleteAlert"
    :isLoading="formStore.deleteFormLoader.isLoading"
    @onClose="openCloseDeleteAlert"
    @onDelete="deleteForms"/>

  <FormOrderDialog :visible="showFormOrder" v-if="showFormOrder" @onClose="openFormOrder"/>
</template>

<style scoped lang="scss">
.forms-list {
  padding: 48px 100px 40px 80px;
  height: 100%;
}
.forms-wrapper {
  max-width: 1400px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.header-buttons {
  display: flex;
}
.forms-table {
  flex: 1;
  overflow: hidden;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
.hover-buttons {
  visibility: hidden;
}
:deep(.p-row-even) {
  cursor: pointer;
  &:hover {
    .hover-buttons {
      visibility: visible;
    }
  }
}
:deep(.p-row-odd) {
  cursor: pointer;
  &:hover {
    .hover-buttons {
      visibility: visible;
    }
  }
}
.create-context-wrapper {
  display: flex;
  align-items: center;
  margin: 16px 0;
}

.empty-content-container {
  padding: 50px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  .empty-content {
    text-align: center;
    .file-icon {
      font-size: 100px;
      margin: 16px 0px;
    }

    .no-entries-label {
      font-weight: bold;
    }

    .new-entry {
      margin: 16px 0px;
      display: flex;
      align-items: center;
    }
  }
}
// This is for new entry button, but this might cause issues if we add a new menu in this component.
:deep(.p-menu, .p-menu-list) {
  max-height: 50vh;
  max-width: 400px;
  overflow-y: auto;
}
</style>
